/**
 * Validasi nomor handphone seluler Indonesia untuk dipakai lintas halaman.
 *
 * Normalisasi ke digit internasional (tanpa +): `62` + nomor nasional tanpa 0 di depan.
 * Standar: E.164 ID = +62 + NSN; `+` tidak dihitung, angka `62` termasuk dalam total digit.
 */

/**
 * @param {string|null|undefined} phone
 * @returns {string|null} Digit internasional tanpa `+`, atau null jika tidak bisa dinormalisasi.
 */
export function normalizeIndonesianMobileDigits(phone) {
  const d = String(phone ?? "").replace(/\D/g, "");
  if (!d) return null;
  if (d.startsWith("62")) return d;
  if (d.startsWith("08")) return `62${d.slice(1)}`;
  if (d.startsWith("8") && d.length >= 9) return `62${d}`;
  return null;
}

function hasTooFewUniqueDigits(digits) {
  return new Set(digits.split("")).size < 3;
}

function hasRepeatedSingleDigitPattern(digits) {
  return /^(\d)\1+$/.test(digits);
}

/**
 * HP seluler Indonesia: minimal 11 digit angka setelah normalisasi ke `62…`;
 * pola `62` + `8[1-9]` + 7–10 digit (08xx / 628xx).
 *
 * @param {string|null|undefined} phone
 * @returns {boolean}
 */
export function isValidIndonesianMobile(phone) {
  const d = normalizeIndonesianMobileDigits(phone);
  if (!d || d.length < 11) return false;
  if (!/^62(8[1-9]\d{7,10})$/.test(d)) return false;

  const nationalNumber = d.slice(2);
  if (hasRepeatedSingleDigitPattern(nationalNumber)) return false;
  if (hasTooFewUniqueDigits(nationalNumber)) return false;

  return true;
}

/** Pesan default untuk input yang tidak lolos validasi. */
export const INDONESIAN_MOBILE_PHONE_INVALID_MESSAGE =
  "Nomor handphone tidak valid. Gunakan format 08…, 628…, atau +62… (minimal 11 digit angka).";
